import { Link, useNavigate } from "react-router-dom";
import svg19 from "../assets/svg/svg19.svg";
import svg33 from "../assets/svg/svg33.svg";
import svg34 from "../assets/svg/svg34.svg";
import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../features/auth/authSlice";
import { useEffect, useState } from "react";
import { Dropdown, Tooltip } from "antd";
import {
  cleanResellerId,
  handleSideMenuCollapse,
} from "../features/global/globalSlice";
import MobileDrawer from "./MobileDrawer";
import { cleanCurrent } from "../features/sms-request/smsRequestSlice";
import {
  cleanBalanceHeader,
  fetchAccountBalance,
  fetchResellerBalance,
  fetchTopBalance,
} from "../features/menu/menuSlice";
import { cashConverter, getSubdomain, numberWithCommas } from "../utils";
import SmartgateGlyph from "../components/SmartgateGlyph";
import { getAuthBrandConfig } from "../pages/auth/authBrand";

export default function Header({ embedded = false }) {
  const { user } = useSelector((state) => state.auth);
  const { balanceHeader } = useSelector((state) => state.menu);
  const { sideMenuCollapsed } = useSelector((state) => state.global);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [subdomain, setSubdomain] = useState("");
  const [open, setOpen] = useState(false);

  async function clean() {
    await dispatch(cleanCurrent());
  }

  async function handleLogout(e) {
    e.preventDefault();
    await dispatch(logout());
    await dispatch(cleanBalanceHeader());
    await dispatch(cleanResellerId());
    await localStorage.clear();
    await clean();
    await navigate("/");
  }

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleSideCollapse = () => {
    dispatch(handleSideMenuCollapse(!sideMenuCollapsed));
  };

  async function fetchBalanceData() {
    if (user?.layer === "RESELLER") {
      dispatch(fetchResellerBalance());
    }
    if (user?.layer === "ACCOUNT") {
      dispatch(fetchAccountBalance());
    }
    if (user?.layer === "TOP") {
      dispatch(fetchTopBalance());
    }
  }

  useEffect(() => {
    fetchBalanceData();
  }, []);

  useEffect(() => {
    setSubdomain(getSubdomain());
  }, []);

  const brandConfig = getAuthBrandConfig(subdomain);

  const accountLabel =
    user?.layer === "TOP" && subdomain === "smartgate"
      ? "Smartgate"
      : balanceHeader?.accName;

  const items = [
    {
      label: (
        <>
          <div className="weiser-profile-dropdown">
            <div className="weiser-profile-summary">
              <div className="weiser-profile-summary-top">
                <div className="weiser-profile-avatar-shell">
                  <img
                    className="weiser-profile-avatar"
                    src="https://simplyilm.com/wp-content/uploads/2017/08/temporary-profile-placeholder-1.jpg"
                    alt="profile avatar"
                  />
                </div>

                <div className="weiser-profile-meta">
                  <span className="weiser-profile-email">{user?.sub}</span>
                  <span className="weiser-profile-meta-line">
                    {accountLabel || "Workspace"} · {user?.layer}
                  </span>
                </div>
              </div>
            </div>

            <div className="profile_dropdown_card weiser-profile-actions">
              <Link
                to="/profile"
                className="view_profile_text weiser-profile-action"
              >
                <UserOutlined />
                <span>View profile</span>
              </Link>

              <Link
                to="/account-settings"
                className="weiser-profile-action"
              >
                <SettingOutlined />
                <span className="profile_text">Account settings</span>
              </Link>

              <button
                onClick={handleLogout}
                className="weiser-profile-action weiser-profile-action-danger"
              >
                <LogoutOutlined />
                <span className="profile_text">Log out</span>
              </button>
            </div>
          </div>
        </>
      ),
      key: "0",
    },
  ];

  return (
    <>
      <MobileDrawer onClose={onClose} open={open} />
      <div
        className={`weiser-app-header ${
          embedded ? "weiser-app-header-embedded" : ""
        } w-full items-center justify-between sticky top-0 z-[1000] py-[.5rem] lg:px-[1.875rem] px-[10px]`}
      >
        <div className="weiser-app-header-left flex items-center cursor-pointer">
          <div
            className={`weiser-app-brand flex items-center cursor-pointer ${
              embedded ? "weiser-embedded-header-brand" : ""
            }`}
          >
            {!embedded && (
              <div className="lg:mr-4 mr-0">
                <img
                  src={svg19}
                  alt="svg19"
                  onClick={handleSideCollapse}
                  className="lg:block hidden"
                />
              </div>
            )}

            <span
              className={`weiser-app-brand-title font-bold text-[25px] ${
                subdomain === "synqafrica" || subdomain == "synqtel"
                  ? "!text-syncBtn"
                  : "text-darkGreen"
              } dash-title flex items-center`}
            >
              {brandConfig.useSmartgateMark ? (
                <span className="weiser-app-brand-mark" aria-hidden="true">
                  <SmartgateGlyph className="weiser-smartgate-glyph" />
                </span>
              ) : (
                <img
                  loading="lazy"
                  decoding="async"
                  src={brandConfig.logo}
                  alt="logo"
                  className={`${
                    subdomain === "synqtel" ? "h-[5vh]" : "h-[7vh]"
                  } object-contain`}
                />
              )}
              {accountLabel}
            </span>
          </div>
        </div>

        {user?.layer != "TOP" && (
          <div className="weiser-app-balance-row weiser-header-balance-desktop gap-x-5">
            {user?.layer != "RESELLER" && (
              <>
                <div
                  className={`weiser-balance-pill ${
                    balanceHeader?.accStatus === "OUT_OF_CREDIT"
                      ? "weiser-balance-pill-alert"
                      : ""
                  } px-3 py-2 text-18 font-dmSans`}
                >
                  Sms Bal: {cashConverter(balanceHeader?.accBalance)}
                </div>
                <div
                  className={`weiser-balance-pill ${
                    balanceHeader?.accStatus === "OUT_OF_CREDIT"
                      ? "weiser-balance-pill-alert"
                      : ""
                  } px-3 py-2 text-[18px] font-dmSans`}
                >
                  Unit: {numberWithCommas(balanceHeader?.accUnits)}
                </div>
              </>
            )}

            {(user?.layer === "RESELLER" || user?.layer === "TOP") && (
              <div
                className={`weiser-balance-pill ${
                  balanceHeader?.rsAllocatableMsgBal < 2000
                    ? "weiser-balance-pill-alert"
                    : ""
                } px-3 py-2 text-[18px] font-dmSans`}
              >
                Units Balance: {numberWithCommas(balanceHeader?.rsAllocatableMsgBal)}
              </div>
            )}
          </div>
        )}

        <div className="weiser-app-header-actions gap-x-[1.25rem] flex items-center cursor-pointer">
          <div className="weiser-header-layer-desktop gap-x-[20px]">
            <span className="flex items-center gap-x-3">
              <span
                className={`weiser-layer-label font-bold text-[16px] ${
                  subdomain === "synqafrica" || subdomain === "synqtel"
                    ? "text-syncBtn"
                    : "text-darkGreen"
                } dash-title`}
              >
                {user?.layer}
              </span>
              <Tooltip placement="bottom" title={balanceHeader?.accStatus}>
                {balanceHeader?.accStatus === "OUT_OF_CREDIT" ? (
                  <img src={svg33} alt={svg33} />
                ) : (
                  <img src={svg34} alt={svg34} />
                )}
              </Tooltip>
            </span>
          </div>

          <img
            src={svg19}
            alt="svg19"
            className="weiser-mobile-menu-trigger weiser-header-mobile-trigger"
            onClick={showDrawer}
          />

          <button className="green-border-btn weiser-outline-chip weiser-header-desktop-chip !items-center !justify-center">
            <span>Individual account</span>
          </button>

          <Dropdown
            overlayClassName="weiser-profile-dropdown-menu"
            menu={{
              items,
            }}
            trigger={["click"]}
          >
            <div className="weiser-profile-trigger lg:pr-[1.25rem] pr-0">
              <img
                className="w-[3.0625rem] h-[3.0625rem] rounded-full object-contain"
                src="https://simplyilm.com/wp-content/uploads/2017/08/temporary-profile-placeholder-1.jpg"
                alt="profile avatar"
              />
            </div>
          </Dropdown>
        </div>
      </div>
    </>
  );
}
